import {
  INELIGIBLE_REASONS,
  type IneligibleReason,
  type RecoverableCheckoutLine,
  type RecoverableCheckoutsList,
  type RecoverableCheckoutsSummary,
  type RecoveryQuery,
  type RecoveryReason,
  type RecoveryScope,
  type RecoveryTally,
  type RecoveryWeek,
} from '@/api/recovery'

/**
 * The demo tenant's unfinished orders: a fixed weekly pattern, so the summary,
 * the list and the CSV are one dataset and a test can count on its shape. Roughly
 * the size 676 measured on 23 Sep 2026 — about fifteen failed or cancelled orders
 * a week — with every reason represented.
 */
const WEEKLY_PATTERN: readonly { reason: RecoveryReason; competition: string; valueMinor: number }[] = [
  { reason: 'eligible', competition: 'Win a Golf GTI', valueMinor: 2495 },
  { reason: 'eligible', competition: '£5,000 Tax-Free Cash', valueMinor: 995 },
  { reason: 'eligible', competition: 'PS5 Pro Bundle', valueMinor: 1490 },
  { reason: 'eligible', competition: 'Win a Golf GTI', valueMinor: 4990 },
  { reason: 'eligible', competition: 'Rolex Submariner', valueMinor: 1995 },
  { reason: 'paid_since', competition: '£5,000 Tax-Free Cash', valueMinor: 1990 },
  { reason: 'paid_since', competition: 'PS5 Pro Bundle', valueMinor: 745 },
  { reason: 'competition_closed', competition: 'Midweek £1,000', valueMinor: 1500 },
  { reason: 'guest', competition: 'Win a Golf GTI', valueMinor: 2495 },
  { reason: 'guest', competition: 'Rolex Submariner', valueMinor: 3990 },
  { reason: 'guest', competition: '£5,000 Tax-Free Cash', valueMinor: 995 },
  { reason: 'suppressed', competition: 'PS5 Pro Bundle', valueMinor: 1490 },
  { reason: 'no_email_consent', competition: 'Win a Golf GTI', valueMinor: 999 },
  { reason: 'no_email_consent', competition: 'Rolex Submariner', valueMinor: 1995 },
  { reason: 'no_email_consent', competition: 'Midweek £1,000', valueMinor: 500 },
]

const DAY_MS = 86_400_000

function mondayOf(date: Date): Date {
  const day = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const offset = (day.getUTCDay() + 6) % 7
  return new Date(day.getTime() - offset * DAY_MS)
}

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function emptyTally(): RecoveryTally {
  return { count: 0, valueMinor: 0, otherCurrencyCount: 0 }
}

function emptyWeek(weekStart: string): RecoveryWeek {
  const byReason = {} as Record<IneligibleReason, RecoveryTally>
  for (const reason of INELIGIBLE_REASONS) byReason[reason] = emptyTally()
  return { weekStart, eligible: emptyTally(), ineligible: emptyTally(), byReason }
}

function add(tally: RecoveryTally, line: RecoverableCheckoutLine) {
  tally.count += 1
  tally.valueMinor += line.valueMinor
}

export class MockRecoveryStore {
  constructor(
    private readonly now: () => Date,
    private readonly currency: () => string,
  ) {}

  private scope(query: RecoveryQuery): RecoveryScope {
    const environments = query.environments.length > 0 ? [...query.environments] : ['production' as const]
    return {
      environments,
      nonProduction: environments.some((environment) => environment !== 'production'),
      currency: this.currency(),
      timezone: 'Europe/London',
      minAgeMinutes: 30,
      generatedAt: this.now().toISOString(),
      truncated: false,
      completeFrom: null,
    }
  }

  /** Newest first, as the API returns them. */
  lines(query: RecoveryQuery): RecoverableCheckoutLine[] {
    const now = this.now()
    const thisMonday = mondayOf(now)
    const lines: RecoverableCheckoutLine[] = []
    let external = 48_000
    for (let week = 0; week < query.weeks; week += 1) {
      const monday = new Date(thisMonday.getTime() - week * 7 * DAY_MS)
      WEEKLY_PATTERN.forEach((entry, index) => {
        // Spread over the week; the current week only up to an hour ago.
        const failedAt = new Date(monday.getTime() + (index * 7 * DAY_MS) / WEEKLY_PATTERN.length + 3_600_000)
        if (failedAt.getTime() > now.getTime() - 3_600_000) return
        external += 1
        const guest = entry.reason === 'guest'
        lines.push({
          orderId: `ord_demo_${external}`,
          externalOrderId: String(external),
          websiteId: 'web_demo_production',
          environment: 'production',
          status: index % 3 === 0 ? 'cancelled' : 'failed',
          failedAt: failedAt.toISOString(),
          weekStart: dateKey(monday),
          customerId: guest ? null : `cus_demo_${index}`,
          externalCustomerId: guest ? null : String(900 + index),
          competitionId: `cmp_demo_${entry.competition.length}`,
          externalCompetitionId: String(700 + entry.competition.length),
          competitionName: entry.competition,
          quantity: Math.max(1, Math.round(entry.valueMinor / 499)),
          valueMinor: entry.valueMinor,
          currency: this.currency(),
          reason: entry.reason,
        })
      })
    }
    return lines.sort((a, b) => b.failedAt.localeCompare(a.failedAt))
  }

  summary(query: RecoveryQuery): RecoverableCheckoutsSummary {
    const thisMonday = mondayOf(this.now())
    const weeks = new Map<string, RecoveryWeek>()
    for (let week = query.weeks - 1; week >= 0; week -= 1) {
      const key = dateKey(new Date(thisMonday.getTime() - week * 7 * DAY_MS))
      weeks.set(key, emptyWeek(key))
    }
    const first = [...weeks.keys()][0] ?? dateKey(thisMonday)
    const totals = emptyWeek(first)
    for (const line of this.lines(query)) {
      for (const target of [weeks.get(line.weekStart), totals]) {
        if (!target) continue
        if (line.reason === 'eligible') {
          add(target.eligible, line)
        } else {
          add(target.ineligible, line)
          add(target.byReason[line.reason], line)
        }
      }
    }
    return { ...this.scope(query), weeks: [...weeks.values()], totals }
  }

  list(query: RecoveryQuery): RecoverableCheckoutsList {
    const matching = this.lines(query).filter((line) => !query.reason || line.reason === query.reason)
    const offset = query.offset ?? 0
    const limit = query.limit ?? 50
    return {
      ...this.scope(query),
      total: matching.length,
      limit,
      offset,
      rows: matching.slice(offset, offset + limit),
    }
  }

  csv(query: RecoveryQuery): Blob {
    const header = 'failed_at,week_start,reason,status,external_order_id,competition_name,quantity,value,currency'
    const rows = this.lines(query)
      .filter((line) => !query.reason || line.reason === query.reason)
      .map((line) =>
        [
          line.failedAt,
          line.weekStart,
          line.reason,
          line.status,
          line.externalOrderId,
          `"${line.competitionName ?? ''}"`,
          line.quantity,
          (line.valueMinor / 100).toFixed(2),
          line.currency,
        ].join(','),
      )
    return new Blob([[header, ...rows].join('\n') + '\n'], { type: 'text/csv' })
  }
}
